import React, { useCallback, useState } from 'react';
import { MDBBtn, MDBIcon, MDBTabs, MDBTabsItem, MDBTabsLink, MDBTabsContent, MDBTabsPane, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalBody, MDBModalTitle } from 'mdb-react-ui-kit';
import ReactCountryFlag from "react-country-flag";
import { useTranslation } from 'react-i18next';
import TiptapEditor from './TiptapEditor';

/**
 * Optional (additive) props - without them the editor behaves exactly as before:
 * - language: 'de' | 'en' controls the visible language (e.g. driven by a page-level
 *   language switch); onLanguageChange is called when a tab is clicked.
 * - hideLanguageTabs: hides the DE/EN tab row and appends the visible language to
 *   the heading. Both editors stay mounted, so switching never loses text.
 */
const BilingualEditor = ({ title, contentDe, contentEn, onChangeDe, onChangeEn, language, onLanguageChange, hideLanguageTabs = false }) => {
  const { t } = useTranslation();
  const [internalLang, setInternalLang] = useState('de');
  const isControlled = language === 'de' || language === 'en';
  const activeLang = isControlled ? language : internalLang;
  const setActiveLang = (lang) => {
    if (!isControlled) setInternalLang(lang);
    if (onLanguageChange) onLanguageChange(lang);
  };
  const [fullscreenEditor, setFullscreenEditor] = useState(null);

  const handleFullscreen = useCallback((editor, lang) => {
    setFullscreenEditor({ lang, editor });
  }, []);

  const closeFullscreen = useCallback(() => {
    setFullscreenEditor(null);
  }, []);

  const hasContentDe = contentDe && contentDe !== '<p></p>' && contentDe !== '';
  const hasContentEn = contentEn && contentEn !== '<p></p>' && contentEn !== '';

  return (
    <div className="bilingual-editor mb-4">
      <h6 className="mb-2">
        {t(title)}
        {hideLanguageTabs && ` (${activeLang === 'en' ? t('English') : t('German')})`}
      </h6>

      {!hideLanguageTabs && (
        <MDBTabs className="mb-2">
          <MDBTabsItem>
            <MDBTabsLink onClick={() => setActiveLang('de')} active={activeLang === 'de'}>
              <ReactCountryFlag countryCode="de" svg style={{ width: "16px", height: "16px" }} className="me-1" />
              {t('German')}
              {hasContentDe && <span className="content-indicator has-content ms-2" />}
            </MDBTabsLink>
          </MDBTabsItem>
          <MDBTabsItem>
            <MDBTabsLink onClick={() => setActiveLang('en')} active={activeLang === 'en'}>
              <ReactCountryFlag countryCode="gb" svg style={{ width: "16px", height: "16px" }} className="me-1" />
              {t('English')}
              {hasContentEn && <span className="content-indicator has-content ms-2" />}
            </MDBTabsLink>
          </MDBTabsItem>
        </MDBTabs>
      )}

      <MDBTabsContent>
        <MDBTabsPane open={activeLang === 'de'}>
          <TiptapEditor
            content={contentDe}
            onChange={onChangeDe}
            onFullscreen={(editor) => handleFullscreen(editor, 'de')}
          />
        </MDBTabsPane>
        <MDBTabsPane open={activeLang === 'en'}>
          <TiptapEditor
            content={contentEn}
            onChange={onChangeEn}
            onFullscreen={(editor) => handleFullscreen(editor, 'en')}
          />
        </MDBTabsPane>
      </MDBTabsContent>

      {/* Fullscreen Modal */}
      <MDBModal
        open={!!fullscreenEditor}
        onClose={closeFullscreen}
        tabIndex="-1"
        className="tiptap-fullscreen-modal"
      >
        <MDBModalDialog size="fullscreen">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                {t(title)} — {fullscreenEditor?.lang === 'de' ? (
                  <><ReactCountryFlag countryCode="de" svg style={{ width: "20px", height: "20px" }} /> {t('German')}</>
                ) : (
                  <><ReactCountryFlag countryCode="gb" svg style={{ width: "20px", height: "20px" }} /> {t('English')}</>
                )}
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={closeFullscreen} type="button" />
            </MDBModalHeader>
            <MDBModalBody>
              {fullscreenEditor?.lang === 'de' && (
                <TiptapEditor content={contentDe} onChange={onChangeDe} />
              )}
              {fullscreenEditor?.lang === 'en' && (
                <TiptapEditor content={contentEn} onChange={onChangeEn} />
              )}
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </div>
  );
};

export default BilingualEditor;
